// BUG-2611: a new cart takes the customer of its sale type (saleType.customer) and falls
// back to the store's default customer (csBunit.b2cCustomer). Both are walk-in records
// shared by every till that bills that sale type or store: GPR's "Walkin Sale" carries
// customer 2001 at every store. The customer bars compared only with the store default,
// so at a store whose default differs 2001 showed by name with an Edit icon, and one
// Update rewrote it for every store (39 live edits, 2026-08-19 to 2026-10-13).
import { useEffect, useState } from "react";
import db from "../database";
import { getPosSaletypes } from "../services";
import usePOSConfigStore from "../stores/usePOSConfigStore";

export const WALK_IN_EDIT_BLOCKED_MESSAGE = "Walk-in customer cannot be edited. Please add a new customer.";

const idsOf = (customer) => [customer?.cwrCustomerId, customer?.b2cCustomerId].filter(Boolean);

// Customer ids of every sale type on this till (db.posSaletypes, filled by syncPosSaleTypes).
// The table holds a handful of rows; it is read on bar mount, on a sale-type change and at
// save, never on a timer.
let saleTypeCustomerIdsCache = [];

export const loadSaleTypeCustomerIds = async () => {
  try {
    const rows = await db.posSaletypes.toArray();
    saleTypeCustomerIdsCache = [...new Set(rows.flatMap((row) => idsOf(row?.customer)))];
  } catch (error) {
    console.error("[BUG-2611] Could not read sale-type customers:", error);
  }
  return saleTypeCustomerIdsCache;
};

// BUG-2611 (2nd fix): db.posSaletypes holds only this till's catalogue. A shared walk-in of
// another store (its default, or a sale type of its catalogue) found by customer search looked
// like a normal customer: live Chocolala 2026-10-14, Khalidiya rewrote Atayeb's "Online Sale"
// customer. Read every store's default and every catalogue's sale-type customers from the
// server. Both reads write nothing (getPosSaletype logs a sync only when a tillId and
// uniqueId are sent). Kept for 30 minutes; a failed refresh keeps the last list.
const CLIENT_WALK_IN_TTL_MS = 30 * 60 * 1000;
let clientWalkIn = { ids: null, at: 0, pending: null };

const fetchClientWalkInCustomerIds = async () => {
  const bunitResponse = await getPosSaletypes({ query: `query { getBunit { csBunitId cwrPcatalogueId b2cCustomer { b2cCustomerId } } }` });
  const bunits = bunitResponse?.data?.data?.getBunit;
  if (!Array.isArray(bunits)) throw new Error(bunitResponse?.data?.errors?.[0]?.message || "getBunit returned no stores");
  const ids = new Set(bunits.map((bunit) => bunit?.b2cCustomer?.b2cCustomerId).filter(Boolean));
  const catalogueIds = [...new Set(bunits.map((bunit) => bunit?.cwrPcatalogueId).filter(Boolean))];
  await Promise.all(
    catalogueIds.map(async (catalogueId) => {
      const response = await getPosSaletypes({ query: `query { getPosSaletype(catalogueId: "${catalogueId}") { customer { cwrCustomerId b2cCustomerId } } }` });
      const rows = response?.data?.data?.getPosSaletype;
      if (!Array.isArray(rows)) throw new Error(response?.data?.errors?.[0]?.message || `getPosSaletype returned nothing for catalogue ${catalogueId}`);
      rows.forEach((row) => idsOf(row?.customer).forEach((id) => ids.add(id)));
    })
  );
  return [...ids];
};

// Every shared walk-in id of the client, or null when it has never been read (offline,
// server error): the caller then has only this till's own walk-ins.
export const loadClientWalkInCustomerIds = async () => {
  if (clientWalkIn.ids && Date.now() - clientWalkIn.at < CLIENT_WALK_IN_TTL_MS) return clientWalkIn.ids;
  if (!clientWalkIn.pending) {
    clientWalkIn.pending = fetchClientWalkInCustomerIds()
      .then((ids) => {
        clientWalkIn = { ids, at: Date.now(), pending: null };
        return ids;
      })
      .catch((error) => {
        console.error("[BUG-2611] Could not read the client's walk-in customers:", error);
        clientWalkIn.pending = null;
        return clientWalkIn.ids;
      });
  }
  return clientWalkIn.pending;
};

// Tests only: forget the cached client list.
export const resetClientWalkInCustomerIds = () => {
  clientWalkIn = { ids: null, at: 0, pending: null };
};

// Store default + every sale type's customer + the selected sale type's customer (kept in
// the persisted store, so it is known before db.posSaletypes is read).
export const getWalkInCustomerIds = (tillData, saleTypeCustomerIds = saleTypeCustomerIdsCache) =>
  new Set([...idsOf(tillData?.tillAccess?.csBunit?.b2cCustomer), ...idsOf(usePOSConfigStore.getState().saleTypeData?.customer), ...(saleTypeCustomerIds || [])]);

// True when the customer is no customer of its own: none, the store default, or a sale
// type's customer. Such a customer is never shown with View / Edit and never edited.
export const isWalkInCustomer = (customer, tillData, saleTypeCustomerIds) => {
  if (!customer?.cwrCustomerId) return true;
  return getWalkInCustomerIds(tillData, saleTypeCustomerIds).has(customer.cwrCustomerId);
};

// For the customer bars: re-renders once the sale-type customers are read, and again when
// the cashier picks another sale type.
export const useSaleTypeCustomerIds = () => {
  const saleTypeData = usePOSConfigStore((s) => s.saleTypeData);
  const [ids, setIds] = useState(saleTypeCustomerIdsCache);
  useEffect(() => {
    let active = true;
    loadSaleTypeCustomerIds().then((loaded) => {
      if (active) setIds(loaded);
    });
    return () => {
      active = false;
    };
  }, [saleTypeData]);
  return ids;
};
